import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Simple MobX-driven modals",
  "description": "",
  "published": "2016-10-28T00:00:00.000Z",
  "redirect_from": ["/blog/simple-mobx-driven-modals/swizec/7166"],
  "categories": "coding, Front End, MobX, react, reactjs, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/10/modal-inception.gif",
        "alt": null
      }}></img></p>
    <p>{`Continuing from yesterday's article about `}<a parentName="p" {...{
        "href": "link"
      }}>{`using MobX with create-react-app`}</a>{`, we're going to build a simple MobX-driven modal. Why? Because I just did it at The Day Job™, and I think it's neat.`}</p>
    <p>{`If you don't think it's neat, that's okay. It's neat anyway.`}</p>
    <p>{`Ingredients:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/facebookincubator/create-react-app"
        }}>{`create-react-app`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/mobxjs/mobx"
        }}>{`MobX`}</a>{` and `}<a parentName="li" {...{
          "href": "https://github.com/mobxjs/mobx-react"
        }}>{`MobX React`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/legitcode/modal"
        }}>{`simple-react-modal`}</a></li>
    </ul>
    <p>{`You can see the full code on GitHub `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/mobx-modal"
      }}>{`here`}</a>{`. There's a link to a compiled&hosted version. I'm still figuring out why the component inception from the gif doesn't work after compiling. `}<a parentName="p" {...{
        "href": "mailto:swizec@swizec.com"
      }}>{`Let me know`}</a>{` if you have any ideas.`}</p>
    <p>{`We're going to build a modals system that lets you:`}</p>
    <ul>
      <li parentName="ul">{`easily open a modal from anywhere in the code`}</li>
      <li parentName="ul">{`specify general modal styling/behavior just once`}</li>
      <li parentName="ul">{`define modal contents at call point`}</li>
      <li parentName="ul">{`tie modals to URLs if needed`}</li>
    </ul>
    <p>{`All of this might sound easy to you, but it's harder than it looks. It `}<em parentName="p">{`sounds`}</em>{` easy, and it's frustrating as hell every damn time you do it. I've done it more times than I dare count.`}</p>
    <p>{`MobX with React has been the easiest and most flexiblest approach I've ever used.`}</p>
    <p>{`You start with a store.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/App.js for example purposes
class Store {
  @observable modal = {
    show: false,
    body: null,
  };

  @action showModal(body) {
    this.modal.show = true;
    this.modal.body = body;
  }

  @action closeModal() {
    this.modal.show = false;
    this.modal.body = null;
  }
}
`}</code></pre>
    <p>{`Your real life data store would be bigger and have more properties and more actions. This is an example.`}</p>
    <p>{`We have an `}<inlineCode parentName="p">{`@observable`}</inlineCode>{` property `}<inlineCode parentName="p">{`modal`}</inlineCode>{`, which has a boolean to denote whether the modal is shown, and a variable for the modal body. Ideally, it's a React component, but you can use anything that React's engine knows how to render.`}</p>
    <p>{`MobX uses the `}<inlineCode parentName="p">{`@observable`}</inlineCode>{` decorator to create deep getters and setters for object properties. This allows the MobX engine to know when properties are dereferenced (read or set), and run relevant observers.`}</p>
    <p>{`We use the `}<inlineCode parentName="p">{`@action`}</inlineCode>{` decorator to say that a function is an action. I don't know if this does anything practical, but it makes your code better.`}</p>
    <p>{`If you only change store properties in actions, then you can model your application as a state machine. If you can model your application as a state machine, then you can understand what it's doing and your life will be easier.`}</p>
    <p>{`Limiting yourself to performing changes in actions increases boilerplateiness a little tiny bit, and gives you this in return:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/10/mobx-debugging.gif",
        "alt": null
      }}></img></p>
    <p>{`Trust me, you want it. ?`}</p>
    <p>{`In `}<inlineCode parentName="p">{`App.js`}</inlineCode>{`, you add a helper function or two. These aren't necessary, but make the markup code cleaner. You'll see.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/App.js

@observer
class App extends Component {
    store = new Store();

        // this is useful for debugging only
    componentDidMount() {
        window.store = this.store;
    }

    openModal1() {
        this.store.showModal(<strong>Strong Hello</strong>);
    }

    openModal2() {
        this.store.showModal(<app>);
    }
</app>
`}</code></pre>
    <p>{`Note that your `}<inlineCode parentName="p">{`App`}</inlineCode>{` must be an `}<inlineCode parentName="p">{`@observer`}</inlineCode>{`. This allows the MobX engine to infer which data store changes are interesting to your component.`}</p>
    <p>{`In our example, the `}<inlineCode parentName="p">{`openModal`}</inlineCode>{` functions invoke the `}<inlineCode parentName="p">{`this.store.showModal`}</inlineCode>{` action and give it some JSX as the modal body. That's the nice thing about HTML as a first-class citizen: You can pass it around any way you want. Neat ?`}</p>
    <p>{`For the modal component itself, we're using `}<inlineCode parentName="p">{`simple-react-modal`}</inlineCode>{` because it's got a clean API and makes it easy to override the styling. Ask your designer; I'm sure they want custom styling if they can get it.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/App.js -> render()

<modal
  show={this.store.modal.show}
  closeonouterclick={true}
  onclose={this.store.closeModal.bind(this.store)}
>
  <a onclick={this.store.closeModal.bind(this.store)} style={closeStyle}>
    X
  </a>

  {this.store.modal.body}
</modal>
`}</code></pre>
    <p>{`We're deferring to `}<inlineCode parentName="p">{`simple-react-modal`}</inlineCode>{` to provide the `}<inlineCode parentName="p">{`Modal`}</inlineCode>{` component and take care of the basic styling. Dark backdrop, centered white rectangle, that sort of thing.`}</p>
    <p>{`We tell it to show itself via the `}<inlineCode parentName="p">{`show`}</inlineCode>{` prop, which we feed straight from our data store. To close our modal, we use the `}<inlineCode parentName="p">{`store.closeModal`}</inlineCode>{` action, which sets `}<inlineCode parentName="p">{`show`}</inlineCode>{` to `}<inlineCode parentName="p">{`false`}</inlineCode>{`. Isn't that cool? Flipping a boolean makes a modal show or hide.`}</p>
    <p>{`For full flexibility, we use `}<inlineCode parentName="p">{`this.store.modal.body`}</inlineCode>{` to render the modal's body. React makes it easy to store just about anything in that property. Even `}<inlineCode parentName="p">{`<App />`}</inlineCode>{` itself… but only in debug mode ?`}</p>
    <p>{`And… yeah… that's about it, really. You can open modals from anywhere in your application that has access to the data store. Call `}<inlineCode parentName="p">{`store.showModal(<stuff>)`}</inlineCode>{`, and it shows up. Call `}<inlineCode parentName="p">{`store.closeModal()`}</inlineCode>{`, and it goes away.`}</p>
    <p>{`You could tie it to your routing and make it so users can refresh the page and keep the modal. I wish more webapps did that.`}</p>
    <p>{`PS: if you put the store in `}<inlineCode parentName="p">{`window`}</inlineCode>{`, you can call actions in your browser console and see how your app reacts.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      